import { useCallback, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Box, Stack, Tooltip, Typography } from '@mui/material';
import { GridColDef, GridSortItem, GridSortModel } from '@mui/x-data-grid';
import { format } from 'date-fns';
import { Workbench, WorkbenchStatusEnum } from '~/api/types';
import { WORKBENCH_PAGE_SIZE } from '~/api/v2/endpoints/workbench';
import { useGetProjectQuery, useGetWorkbenchTypesQuery } from '~/api/v2/hooks';
import { Table } from '~/components';
import { TableFilters } from '~/components/Table/components/filters/types';
import { useFiltersWorkbenchConfig, useGetWorkbenchesQuery } from '~/hooks';
import { InfoIcon } from '~/icons';
import { truncateStyles } from '~/pages/StoragePage/components/styles';
import { useAppSelector } from '~/redux';
import { selectCurrentUserApplicationRole, selectCurrentUserId } from '~/redux/slices/currentUserSlice';
import { DATE_FORMAT, getPriviligies } from '~/utils';
import { WorkbenchAutoStopCell, WorkbenchControlCell, WorkbenchStatusCell, WorkbenchTabHeader } from './components';
import { WorkbenchFilters, WorkbenchFiltersType } from './types';

const WorkbenchTab = () => {
  const projectId = +useParams().projectid!;
  const userId = useAppSelector(selectCurrentUserId);
  const userApplicationRole = useAppSelector(selectCurrentUserApplicationRole);
  const [showTerminated, setShowTerminated] = useState<boolean>(false);
  const { data: projectData, isFetching: isProjectDataFetching } = useGetProjectQuery({ projectId });
  const { isFetching: isWorkbenchConfigurationFetching } = useGetWorkbenchTypesQuery({
    projectId,
  });
  const filterOptions = useFiltersWorkbenchConfig({ showTerminated });

  const [searchQuery, setSearchQuery] = useState<string>('');
  const [filters, setFilters] = useState<WorkbenchFilters>({
    [WorkbenchFiltersType.STATUS]: [],
    [WorkbenchFiltersType.TYPE]: [],
    [WorkbenchFiltersType.CREATED_BY]: [],
    [WorkbenchFiltersType.CREATED_ON]: [],
  });
  const [sortModel, setSortModel] = useState<GridSortItem | null>(null);
  const { data: workbenchesData, isFetching: isFetchingManualRefresh } = useGetWorkbenchesQuery({
    projectId,
    showTerminated,
    searchQuery,
    sortModel,
    filters,
  });

  const userProjectRole = projectData?.members?.find(({ id }) => id === userId)?.role_id;
  const hasAccessToOperate =
    getPriviligies(userProjectRole)?.workbenchTab?.canOperate ||
    getPriviligies(userApplicationRole)?.workbenchTab?.canOperate;

  const handleFiltersChange = useCallback((filters: TableFilters) => {
    setFilters(filters as WorkbenchFilters);
  }, []);

  const handleSearchChange = useCallback((query: string) => {
    setSearchQuery(query);
  }, []);

  const handleSortChange = (sortModel: GridSortModel) => {
    setSortModel(sortModel[0] || null);
  };

  const columns: GridColDef<Workbench>[] = [
    {
      field: 'name',
      headerName: 'Name',
      flex: 3,
      renderCell: (params) => (
        <Tooltip title={params.row.name}>
          <Typography sx={truncateStyles}>{params.row.name}</Typography>
        </Tooltip>
      ),
    },
    {
      field: 'controls',
      headerName: '',
      flex: 2,
      sortable: false,
      renderCell: (params) => (
        <Box sx={{ mx: '-2rem' }}>
          <WorkbenchControlCell
            workbenchId={+params?.id}
            workbenchName={params?.row.name}
            status={params?.row?.status}
            instanceId={params?.row?.instance_id}
          />
        </Box>
      ),
    },
    {
      field: 'status',
      headerName: 'Instance status',
      renderCell: (params) => <WorkbenchStatusCell status={params?.value} />,
      flex: 1.5,
    },
    {
      field: 'type',
      headerName: 'Type',
      flex: 2,
    },
    {
      field: 'autostop',
      renderHeader: () => (
        <Tooltip
          title="Turn the auto-stop feature on or off. This feature automatically shuts down a workbench after a set period
              of inactivity"
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography fontSize="1.7rem" fontWeight={600}>
              Auto Stop
            </Typography>
            <InfoIcon />
          </Box>
        </Tooltip>
      ),
      renderCell: (params) => (
        <WorkbenchAutoStopCell
          workbenchId={params.row.id}
          isAutostopEnabled={params.row.autostop}
          disabled={!hasAccessToOperate || params.row.status === WorkbenchStatusEnum.TERMINATED}
        />
      ),
      sortable: false,
      flex: 2,
    },
    {
      field: 'created_by',
      headerName: 'Created by',
      valueGetter: (_, row) => `${row.creator?.first_name} ${row.creator?.last_name}`,
      flex: 1.5,
    },
    {
      field: 'created',
      headerName: 'Created on',
      valueGetter: (_, row) => format(new Date(row.created), DATE_FORMAT),
      flex: 1.5,
    },
  ];

  return (
    <Box p="3rem">
      <Stack spacing={3}>
        <WorkbenchTabHeader
          setShowTerminated={setShowTerminated}
          showTerminated={showTerminated}
          searchQuery={searchQuery}
          filters={filters}
          sortModel={sortModel}
        />
        <Table
          filterOptions={filterOptions}
          isFiltersLoading={isProjectDataFetching && isWorkbenchConfigurationFetching}
          onSearchChange={handleSearchChange}
          onFiltersChange={handleFiltersChange}
          onSortChange={handleSortChange}
          hideFooter
          rows={workbenchesData?.data || []}
          rowCount={workbenchesData?.data.length || 0}
          columns={columns}
          paginationMode="server"
          sortingMode="server"
          filterMode="server"
          initialState={{
            pagination: { paginationModel: { page: 0, pageSize: WORKBENCH_PAGE_SIZE } },
          }}
          loading={isFetchingManualRefresh}
        />
      </Stack>
    </Box>
  );
};

export default WorkbenchTab;
